/**
 * fetch() that always finishes, and retries only when retrying can help.
 *
 * Two facts about this backend shape everything below.
 *
 * A deploy takes the whole server down. There is one EC2 instance and no load
 * balancer, and deploys run `docker-compose down && up`, so for 30–120 seconds
 * there is nothing to talk to. The first stretch has nginx itself down, which
 * the browser reports as `TypeError: Failed to fetch` with no status and a
 * misleading CORS message in the console — the single most common failure here
 * carries no status code at all.
 *
 * And nginx rate-limits at 10r/s keyed on the Cloudflare edge IP, so every
 * customer shares one bucket, and tripping it returns 503 — which is retryable.
 * Synchronised retries would turn a one-minute deploy into a multi-minute
 * self-inflicted outage. Hence full jitter, a hard attempt cap, and a ceiling on
 * how many retries the whole app may have in flight at once.
 *
 * Layering: this wraps `authenticatedFetch` from the outside and never reaches
 * into it. That module already does single-flight 401 refresh with a subscriber
 * queue and replays the request once. 401 is therefore absent from the retry
 * matrix here — see `shouldRetry`.
 */

import { authenticatedFetch } from './apiInterceptor';
import {
  classifyError,
  parseRetryAfter,
  retryAfterWait,
  probeServerAlive,
} from './errorTaxonomy';

/** Requests that outlive this are hopeless; the server's own ceiling is 60s. */
export const TIMEOUT_FAST = 15000;
export const TIMEOUT_NORMAL = 30000;
/**
 * Just under gunicorn's 60s worker timeout and nginx's 60s proxy_read_timeout,
 * so *our* deadline fires first and we can explain it — rather than racing them
 * and getting an ambiguous 504 or a killed worker.
 */
export const TIMEOUT_SLOW = 55000;

const DEFAULT_RETRIES = 3;
const DEFAULT_BASE_DELAY_MS = 500;
const DEFAULT_MAX_DELAY_MS = 8000;

/**
 * nginx's rate limiter answers 503. Backing off from it at the same pace as a
 * normal outage is what keeps the herd synchronised, so 503 waits longer.
 */
const RATE_LIMIT_BASE_DELAY_MS = 1500;

const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/** Raised when the caller aborted on purpose. Never rendered as a failure. */
export class RequestAbortedError extends Error {
  constructor(message = 'Request cancelled') {
    super(message);
    this.name = 'RequestAbortedError';
    this.aborted = true;
  }
}

/** Raised when our own deadline elapsed. Carries no status — none was received. */
export class RequestTimeoutError extends Error {
  constructor(timeoutMs) {
    super(`Request exceeded ${timeoutMs}ms`);
    this.name = 'RequestTimeoutError';
    this.isTimeout = true;
    this.status = 0;
    this.timeoutMs = timeoutMs;
  }
}

// ─── Backoff ──────────────────────────────────────────────────────────────────

/**
 * Full jitter, per AWS's "Exponential Backoff and Jitter":
 *
 *     delay = random(0, min(maxDelayMs, baseDelayMs * 2 ** attempt))
 *
 * Not `exponential ± 10%`. When every client fails at the same instant — which
 * is exactly what a deploy does — a narrow jitter band keeps them in lockstep
 * and they arrive together on the shared rate-limit bucket. Sampling the whole
 * interval is what actually spreads them out.
 *
 * `retryAfterMs` replaces the computed delay rather than adding to it: the
 * server told us when to come back, and second-guessing it just delays recovery.
 *
 * @param {number} attempt  0 for the first retry.
 * @param {object} [opts]
 * @param {() => number} [opts.random]  Injectable for deterministic tests.
 */
export function computeBackoff(attempt, opts = {}) {
  const {
    baseDelayMs = DEFAULT_BASE_DELAY_MS,
    maxDelayMs = DEFAULT_MAX_DELAY_MS,
    retryAfterMs = null,
    random = Math.random,
  } = opts;

  if (retryAfterMs !== null && retryAfterMs !== undefined) {
    return Math.max(0, retryAfterMs);
  }

  const ceiling = Math.min(maxDelayMs, baseDelayMs * Math.pow(2, attempt));
  return Math.floor(random() * ceiling);
}

/**
 * Exponential backoff centred on the target, rather than sampled from zero:
 *
 *     delay = min(maxDelayMs, baseDelayMs * 2 ** attempt) ± jitterMs
 *
 * Full jitter (above) halves the mean wait — a 10s ceiling averages 5s — which
 * is right when several attempts are racing inside one request and finishing
 * early is the whole point. It is wrong for polling a server that is down: if it
 * did not recover in five seconds it is unlikely to recover in seven, so the
 * early probes are spent where they pay off least.
 *
 * Centring keeps the intended pace (5s, 10s, 20s…) and still decorrelates
 * clients: ±2s scatters them across a four-second window, which is ample when
 * the alternative is every open tab firing at exactly the same millisecond.
 *
 * @param {number} attempt   0 for the first probe.
 * @param {object} [opts]
 * @param {number} [opts.jitterMs=2000]  Half-width of the random spread.
 */
export function computeCenteredBackoff(attempt, opts = {}) {
  const {
    baseDelayMs = DEFAULT_BASE_DELAY_MS,
    maxDelayMs = DEFAULT_MAX_DELAY_MS,
    jitterMs = 2000,
    random = Math.random,
  } = opts;

  const target = Math.min(maxDelayMs, baseDelayMs * Math.pow(2, attempt));
  // random() in [0,1) maps to [-jitterMs, +jitterMs).
  const offset = (random() * 2 - 1) * jitterMs;
  // Never negative: a probe scheduled in the past would fire immediately and
  // undo the pacing.
  return Math.max(0, Math.floor(target + offset));
}

// ─── Concurrency ceiling ──────────────────────────────────────────────────────

/**
 * A dashboard can mount a dozen panels at once. If each runs its own retry train
 * into a server with 16 total request slots, our recovery attempts become the
 * thing preventing recovery. Only a few may be waiting to retry at any moment;
 * the rest fail fast and let the user retry by hand.
 */
const MAX_CONCURRENT_RETRIES = 4;
let activeRetries = 0;

function acquireRetrySlot() {
  if (activeRetries >= MAX_CONCURRENT_RETRIES) return false;
  activeRetries += 1;
  return true;
}

function releaseRetrySlot() {
  activeRetries = Math.max(0, activeRetries - 1);
}

/** Exported for tests, which must not leak a slot between cases. */
export function _resetRetryConcurrency() {
  activeRetries = 0;
}

// ─── Sleep that can be cut short ──────────────────────────────────────────────

/**
 * Wait `ms`, unless `signal` aborts first.
 *
 * A pending backoff must stay interruptible: when the user presses "Try now" or
 * navigates away, continuing to sit on a timer would make the UI feel stuck for
 * up to eight seconds after they asked for something else.
 */
function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal && signal.aborted) {
      reject(new RequestAbortedError());
      return;
    }
    const timer = setTimeout(() => {
      if (signal) signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(timer);
      reject(new RequestAbortedError());
    }
    if (signal) signal.addEventListener('abort', onAbort, { once: true });
  });
}

// ─── One attempt ──────────────────────────────────────────────────────────────

/**
 * A single request with its own deadline, forwarding a caller abort.
 *
 * Resolves with the Response even when it is a 4xx/5xx — deciding what a status
 * means belongs to the retry policy, not here. Rejects only when no response
 * arrived: our timeout, a caller abort, or a network-level failure.
 */
async function attemptOnce(url, fetchOptions, { timeoutMs, signal, fetchImpl }) {
  const controller = new AbortController();
  const onAbort = () => controller.abort();

  if (signal) {
    if (signal.aborted) throw new RequestAbortedError();
    signal.addEventListener('abort', onAbort, { once: true });
  }

  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    return await fetchImpl(url, { ...fetchOptions, signal: controller.signal });
  } catch (err) {
    // Our deadline, not the caller's — report it as a timeout so the policy can
    // treat it differently from a deliberate cancel.
    if (timedOut) throw new RequestTimeoutError(timeoutMs);
    if (signal && signal.aborted) throw new RequestAbortedError();
    if (err && err.name === 'AbortError') throw new RequestAbortedError();
    throw err;
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener('abort', onAbort);
  }
}

// ─── Policy ───────────────────────────────────────────────────────────────────

/**
 * Can this request body survive being sent twice?
 *
 * A ReadableStream is consumed by the first attempt and would replay as empty,
 * which is worse than not retrying — the server would accept a silently
 * truncated write. Strings and URLSearchParams are safe. FormData is technically
 * re-sendable but may wrap a stream, so it is refused too.
 */
function isReplayableBody(body) {
  if (body === null || body === undefined) return true;
  if (typeof body === 'string') return true;
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return true;
  return false;
}

/**
 * Whether to try again, given what came back.
 *
 * The 401 case is the sharp edge: `authenticatedFetch` has already refreshed the
 * token and replayed the request by the time we see one, and its refresh is
 * single-flight with a subscriber queue. Retrying here would queue a second
 * refresh and can race the `sessionExpired` event, logging out a user whose
 * token was fine. 401 never retries — asserted in resilientFetch.test.js.
 */
function shouldRetry({ status, err, idempotent }) {
  // No response at all: a deploy with nginx down, or a dropped connection.
  //
  // A timeout is the ambiguous case — we stopped listening, but the request may
  // well have reached the server and committed. Only repeat it when repeating is
  // harmless.
  if (status === 0) {
    if (err && err.isTimeout) return idempotent;
    return idempotent;
  }

  if (status === 401 || status === 403) return false;
  if (status === 404 || status === 409) return false;
  if (status === 400 || status === 422) return false;

  // Application 500s are deterministic bugs; a retry costs a gunicorn slot and
  // returns the same 500.
  if (status === 500) return false;

  // Even a clean 502 does not prove the write was rejected: nginx returns one
  // when gunicorn dies mid-request, which can happen after the transaction
  // committed. Replaying a POST on that evidence risks a duplicate order.
  if (status === 502 || status === 503 || status === 504) return idempotent;
  if (status === 429) return true;

  return false;
}

// ─── Entry point ──────────────────────────────────────────────────────────────

/**
 * fetch with a deadline, jittered backoff, and a status-aware retry policy.
 *
 * Resolves with the final Response — including a failing one, so callers keep
 * their existing `response.ok` handling. Rejects only for a caller abort, or
 * when no response was ever obtained.
 *
 * @param {string} url
 * @param {RequestInit} [fetchOptions]
 * @param {object} [resilience]
 * @param {number}   [resilience.timeoutMs=TIMEOUT_NORMAL]
 * @param {number}   [resilience.retries=3]       Additional attempts after the first.
 * @param {number}   [resilience.baseDelayMs=500]
 * @param {number}   [resilience.maxDelayMs=8000]
 * @param {boolean}  [resilience.idempotent]      Defaults to true for GET/HEAD/OPTIONS.
 *                                                Set explicitly to retry a safe POST.
 * @param {AbortSignal} [resilience.signal]
 * @param {(progress: {attempt: number, maxAttempts: number, delayMs: number,
 *          nextAttemptAt: number, reason: string}) => void} [resilience.onRetry]
 *          Called before each wait, so the UI can show a live countdown instead
 *          of an unexplained pause.
 * @param {typeof fetch} [resilience.fetchImpl=authenticatedFetch]
 */
export async function resilientFetch(url, fetchOptions = {}, resilience = {}) {
  const {
    timeoutMs = TIMEOUT_NORMAL,
    baseDelayMs = DEFAULT_BASE_DELAY_MS,
    maxDelayMs = DEFAULT_MAX_DELAY_MS,
    signal,
    onRetry,
    fetchImpl = authenticatedFetch,
    random,
  } = resilience;

  const method = String(fetchOptions.method || 'GET').toUpperCase();
  const idempotent =
    resilience.idempotent !== undefined
      ? resilience.idempotent
      : IDEMPOTENT_METHODS.has(method);

  let retries = resilience.retries !== undefined ? resilience.retries : DEFAULT_RETRIES;

  // A body we cannot resend must not be retried, whatever the caller asked for.
  if (retries > 0 && !isReplayableBody(fetchOptions.body)) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[resilientFetch] ${method} ${url}: body cannot be replayed (FormData or stream), ` +
          'so retries are disabled for this request.'
      );
    }
    retries = 0;
  }

  const maxAttempts = retries + 1;
  let consecutiveNetworkFailures = 0;
  let lastError = null;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    let response = null;
    let err = null;

    try {
      response = await attemptOnce(url, fetchOptions, { timeoutMs, signal, fetchImpl });
    } catch (caught) {
      // A deliberate cancel is not a failure and must not be retried or reported.
      if (caught instanceof RequestAbortedError) throw caught;
      err = caught;
      lastError = caught;
    }

    if (response && response.ok) return response;

    const status = response ? response.status : 0;
    if (status === 0) {
      consecutiveNetworkFailures += 1;
    } else {
      consecutiveNetworkFailures = 0;
    }

    const isLastAttempt = attempt === maxAttempts - 1;
    if (isLastAttempt || !shouldRetry({ status, err, idempotent })) {
      if (response) return response;
      throw lastError;
    }

    // Honour an explicit Retry-After over our own guess, but never sit on it for
    // longer than a person will tolerate watching a countdown.
    const retryAfterMs = retryAfterWait(
      response && response.headers ? parseRetryAfter(response.headers.get('Retry-After')) : null
    );

    // Two silent attempts in a row means nginx itself is gone, not just busy —
    // worth one cheap probe so the copy can say "deploying" rather than guess.
    let serverAlive = null;
    if (consecutiveNetworkFailures >= 2) {
      serverAlive = await probeServerAlive();
    }

    const classified = classifyError(err || { status }, {
      response,
      serverAlive,
      consecutiveNetworkFailures,
    });

    if (!acquireRetrySlot()) {
      // Too many retry trains already running. Give up rather than pile on; the
      // user still has a visible "Try again".
      if (response) return response;
      throw lastError;
    }

    try {
      const delayMs = computeBackoff(attempt, {
        // 503 is nginx's rate limiter as often as it is an outage; either way it
        // is the one status where we are plausibly the cause.
        baseDelayMs: status === 503 ? Math.max(baseDelayMs, RATE_LIMIT_BASE_DELAY_MS) : baseDelayMs,
        maxDelayMs,
        retryAfterMs,
        random,
      });

      if (typeof onRetry === 'function') {
        onRetry({
          attempt: attempt + 1,
          maxAttempts,
          delayMs,
          nextAttemptAt: Date.now() + delayMs,
          reason: classified.kind,
        });
      }

      await sleep(delayMs, signal);
    } finally {
      releaseRetrySlot();
    }
  }

  // Unreachable: the loop either returns or throws on its last attempt.
  throw lastError || new Error('Request could not be completed');
}

export default resilientFetch;
